import React, { useState, useRef, useEffect } from "react";
import {
  LayoutDashboard,
  Building2,
  LogOut,
  ChevronLeft,
  ChevronRight,
  User,
  ChevronUp,
  AlertTriangle,
  Settings, // Change Shield to Settings
  ChevronDown, // Add for submenu
} from "lucide-react";
import Tooltip from "./Tooltip.tsx";
import { useAuth } from "../contexts/AuthContext.tsx";
import { useNavigate } from "react-router-dom";

interface SidebarProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ activeTab, onTabChange }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isSettingsExpanded, setIsSettingsExpanded] = useState(false); // Add state for settings submenu
  const profileMenuRef = useRef<HTMLDivElement>(null);

  // 根据用户角色动态生成菜单项
  const getTabs = () => {
    const baseTabs = [
      { id: "dashboard", name: "Dashboard", icon: LayoutDashboard },
      { id: "alerts", name: "Device Health Alerts", icon: AlertTriangle },
    ];

    // Hide Customer page for Partner Viewer
    const roleName = user?.role?.name;
    if (roleName && roleName !== "Partner Viewer") {
      baseTabs.push({ id: "organization", name: "Customer", icon: Building2 });
    }

    return baseTabs;
  };

  const getSettingsSubmenus = () => {
    const submenus = [
      {
        id: "settings/alert-rule",
        name: "Alert Rule Settings",
        path: "settings/alert-rule",
      },
    ];

    // Only Partner Admin can see Users
    if (user?.role?.name === "Partner Admin") {
      submenus.unshift({
        id: "settings/users",
        name: "Users",
        path: "settings/users",
      });
    }

    return submenus;
  };

  const tabs = getTabs();
  const settingsSubmenus = getSettingsSubmenus();

  // Check if any settings submenu is active
  const isSettingsActive = activeTab.startsWith("settings/");

  // Auto-expand settings if any submenu is active
  useEffect(() => {
    if (isSettingsActive) {
      setIsSettingsExpanded(true);
    }
  }, [isSettingsActive]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        profileMenuRef.current &&
        !profileMenuRef.current.contains(event.target as Node)
      ) {
        setIsProfileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // Generate initials from first and last name
  const getInitials = () => {
    const firstInitial = user?.firstName?.charAt(0)?.toUpperCase() || "";
    const lastInitial = user?.lastName?.charAt(0)?.toUpperCase() || "";
    return `${firstInitial}${lastInitial}` || "U";
  };

  return (
    <div
      className={`${
        isCollapsed ? "w-20" : "w-64"
      } bg-white border-r border-gray-200 flex flex-col h-full transition-all duration-200`}
    >
      {/* Header + Collapse Button */}
      <div className="flex items-center justify-between p-6 border-b border-gray-200">
        {!isCollapsed && (
          <div>
            <h1 className="text-xl font-bold text-gray-800">IQGuard</h1>
            <p className="text-sm text-gray-600 mt-1">Management Console</p>
          </div>
        )}
        <button
          onClick={() => setIsCollapsed((v) => !v)}
          className="ml-auto p-2 rounded hover:bg-gray-100"
          aria-label={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
        >
          {isCollapsed ? (
            <ChevronRight className="w-5 h-5" />
          ) : (
            <ChevronLeft className="w-5 h-5" />
          )}
        </button>
      </div>

      {/* Navigation Tabs */}
      <div className="flex-1 p-4">
        <nav
          className={`space-y-2 ${
            isCollapsed ? "flex flex-col items-center" : ""
          }`}
        >
          {/* Regular tabs */}
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            const button = (
              <button
                key={tab.id}
                onClick={() => onTabChange(tab.id)}
                className={`flex items-center text-sm font-medium rounded-lg transition-colors ${
                  isActive
                    ? "bg-blue-50 text-blue-700 border border-blue-200"
                    : "text-gray-700 hover:bg-gray-50 hover:text-gray-900"
                } ${
                  isCollapsed
                    ? "w-14 h-14 justify-center"
                    : "w-full space-x-3 py-3 px-4"
                }`}
              >
                <Icon
                  className={`flex-shrink-0 w-6 h-6 ${
                    isActive ? "text-blue-600" : "text-gray-500"
                  }`}
                />
                {!isCollapsed && <span>{tab.name}</span>}
              </button>
            );

            return isCollapsed ? (
              <Tooltip key={tab.id} content={tab.name} position="right">
                {button}
              </Tooltip>
            ) : (
              button
            );
          })}

          {/* Settings menu with submenu - Hidden for Partner Viewer */}
          {user?.role?.name && user.role.name !== "Partner Viewer" && (
            <div className={isCollapsed ? "flex justify-center" : ""}>
              {isCollapsed ? (
                <Tooltip content="Settings" position="right">
                  <button
                    onClick={() => {
                      // For collapsed sidebar, navigate to first available settings page
                      const firstSubmenu = settingsSubmenus[0];
                      if (firstSubmenu) {
                        onTabChange(firstSubmenu.id);
                      }
                    }}
                    className={`flex items-center text-sm font-medium rounded-lg transition-colors w-14 h-14 justify-center ${
                      isSettingsActive
                        ? "bg-blue-50 text-blue-700 border border-blue-200"
                        : "text-gray-700 hover:bg-gray-50 hover:text-gray-900"
                    }`}
                  >
                    <Settings
                      className={`w-6 h-6 ${
                        isSettingsActive ? "text-blue-600" : "text-gray-500"
                      }`}
                    />
                  </button>
                </Tooltip>
              ) : (
                <div>
                  {/* Settings main button */}
                  <button
                    onClick={() => setIsSettingsExpanded(!isSettingsExpanded)}
                    className={`flex items-center text-sm font-medium rounded-lg transition-colors w-full space-x-3 py-3 px-4 ${
                      isSettingsActive
                        ? "bg-blue-50 text-blue-700 border border-blue-200"
                        : "text-gray-700 hover:bg-gray-50 hover:text-gray-900"
                    }`}
                  >
                    <Settings
                      className={`flex-shrink-0 w-6 h-6 ${
                        isSettingsActive ? "text-blue-600" : "text-gray-500"
                      }`}
                    />
                    <span className="flex-1 text-left">Settings</span>
                    <ChevronDown
                      className={`w-4 h-4 transition-transform ${
                        isSettingsExpanded ? "transform rotate-180" : ""
                      } ${
                        isSettingsActive ? "text-blue-600" : "text-gray-500"
                      }`}
                    />
                  </button>

                  {/* Settings submenu */}
                  {isSettingsExpanded && (
                    <div className="mt-1 ml-6 space-y-1">
                      {settingsSubmenus.map((submenu) => (
                        <button
                          key={submenu.id}
                          onClick={() => onTabChange(submenu.id)}
                          className={`flex items-center text-sm font-medium rounded-lg transition-colors w-full py-2 px-4 ${
                            activeTab === submenu.id
                              ? "bg-blue-50 text-blue-700 border border-blue-200"
                              : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                          }`}
                        >
                          <span>{submenu.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </nav>
      </div>

      {/* User Profile */}
      <div
        className="p-4 border-t border-gray-200 relative"
        ref={profileMenuRef}
      >
        <div
          className={`flex items-center cursor-pointer ${
            isCollapsed ? "justify-center" : "space-x-3 p-3"
          } bg-gray-50 rounded-lg`}
          onClick={() => setIsProfileMenuOpen((v) => !v)}
        >
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center flex-shrink-0">
            <span className="text-xs font-bold text-white">
              {getInitials()}
            </span>
          </div>
          {!isCollapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {user ? `${user.firstName} ${user.lastName}` : "Guest"}
              </p>
              <p className="text-xs text-gray-500 truncate">{user?.email}</p>
            </div>
          )}
          {!isCollapsed && (
            <ChevronUp
              className={`w-4 h-4 text-gray-500 transition-transform ${
                isProfileMenuOpen ? "transform rotate-180" : ""
              }`}
            />
          )}
        </div>
        {isProfileMenuOpen && (
          <div className="absolute bottom-full mb-2 w-full bg-white rounded-lg shadow-lg border border-gray-200">
            <button
              onClick={() => {
                navigate("/profile");
                setIsProfileMenuOpen(false);
              }}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
            >
              <User className="w-4 h-4" />
              <span>Profile</span>
            </button>
            <button
              onClick={handleLogout}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
            >
              <LogOut className="w-4 h-4" />
              <span>Logout</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Sidebar;
